import { FetchError, ofetch } from "ofetch";
import { cache as dedupe } from "react";
//@ts-ignore
import formatProductsForPlp from "@/app/products/[...category]/clientUtils/formatProductsForPlp";
import { formatSBCategories } from "@/app/products/[...category]/serverUtils/getInitialPlpData";
import { fetchDepictCategory } from "@/app/products/[...category]/serverUtils/helpers";
import { cacheLife } from "@/cache-life";
import env from "@/env.mjs";
import type { Category } from "@/lib/centraGenerics/categories/getCategories";
import type { DepictOptions } from "@/lib/depict/types";
import { getStory } from "@/lib/storyblok/storyblokSetup";
import type { IFilterGroup, ISortOrderValue } from "@/types/centra";
import type {
  PlpCategoriesStoryblok,
  ProductListingPageStoryblok,
} from "@/types/storyblok-blok-types";
import type { CentraProduct } from "../../centra/atomicSetup";
import { fetchPCByIds } from "../../centra/fetchersSetup";
import type { ProductCard } from "../../centra/formatters/formatProductCards/formatProductCard";
import type {
  DepictDisplay,
  IFormattedFilters,
  ProductListingResponse_internal,
} from "./types";

export type GetProducts = <PC extends ProductCard, ExtraData>({
  cardFormatter,
  category,
  filters,
  productIds,
  search,
  sortOrder,
  page,
  productsPerPage,
  extraDataFetcher,
  customerToken,
  depictUUID,
}: // new
{
  cardFormatter: (product: CentraProduct, data?: ExtraData) => PC;
  category: Category[];
  filters?: IFormattedFilters;
  filterGroups?: IFilterGroup[];
  productIds?: string[] | undefined;
  search?: string;
  sortOrder?: ISortOrderValue[];
  page?: number;
  productsPerPage?: number;
  customerToken?: string;
  depictUUID?: string;
  soldOut?: string;
  apiToken?: string;
  extraDataFetcher?: (productIds: string[]) => Promise<ExtraData>;
}) => Promise<{
  products: PC[];
  filter: IFilterGroup[];
  token: string;
  productCount: number;
}>;

const getDepictProductsByCategory = async (
  options: DepictOptions,
  cursor: string | null,
) => {
  const url = `${env.NEXT_PUBLIC_DEPICT_API_URL}/v3/listings/${options.category}/products`;

  try {
    const res = await ofetch<ProductListingResponse_internal>(url, {
      method: "POST",
      body: JSON.stringify({
        ...options,
        category: undefined,
        cursor: cursor || null,
        merchant: "camillapihl",
      }),
      next: {
        revalidate: cacheLife.FIVE_MINUTES,
      },
    });

    return res;
  } catch (e) {
    if (e instanceof FetchError) {
      console.error("Failed to fetch depict data", e.data);
    }
    return null;
  }
};

export const getDepictProductsByQuery = async (
  options: DepictOptions,
  _cursor?: string | null,
) => {
  const url = `${env.NEXT_PUBLIC_DEPICT_API_URL}/v3/search/results`;
  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      ...options,
    }),
    next: {
      revalidate: cacheLife.FIVE_MINUTES,
    },
  });

  if (!res.ok) return null;
  const data = (await res.json()) as ProductListingResponse_internal;
  return data;
};

export const formatPlpData = async (
  data: ProductListingResponse_internal | null,
) => {
  if (!data?.displays || data.displays.length === 0) {
    return {
      products: [],
      totalCount: 0,
      page: data?.page || 1,
      nextCursor: null,
      sorts: data?.sorts || [],
      filters: data?.filters || [],
      contentLinks: [],
    };
  }

  const depictDisplays = data.displays;

  const productIdToDepictData = new Map<
    string,
    {
      display: DepictDisplay;
      listingResultId: string;
      searchResultId?: string;
    }
  >();

  for (const display of depictDisplays) {
    const rootVariant = display.variant_displays[display.variant_index];
    if (rootVariant?.display_item_id) {
      productIdToDepictData.set(rootVariant.display_item_id, {
        display,
        listingResultId: display.product_listing_result_id,
        searchResultId: display.search_result_id,
      });
    }
  }

  const productIds = Array.from(productIdToDepictData.keys());

  // One batched Centra request for the whole page. Ids are sorted so the
  // data-cache key stays stable when Depict reorders the listing.
  const centraProducts = await fetchPCByIds({
    ids: [...productIds].sort(),
  }).catch(e => {
    console.error("Failed to fetch products from Centra", e);
    return [];
  });

  const centraProductsById = new Map(
    centraProducts.map(product => [String(product.id), product]),
  );

  const enrichedProducts = productIds
    .map(productId => {
      const product = centraProductsById.get(productId);
      if (!product) return null;

      const depictData = productIdToDepictData.get(productId);

      if (!depictData) return product;

      return {
        ...product,
        depictListingResultId: depictData.listingResultId,
        depictSearchResultId: depictData.searchResultId,
      };
    })
    .filter((product): product is ProductCard => product !== null);

  const formattedProductsForPlp = formatProductsForPlp(enrichedProducts, []);

  return {
    products: formattedProductsForPlp,
    totalCount: data.n_hits,
    page: data.page,
    nextCursor: data.cursor,
    sorts: data.sorts || [],
    filters: data.filters || [],
    contentLinks: [],
  };
};

export const getPlpData = async (
  options: DepictOptions,
  categories?: PlpCategoriesStoryblok[] | undefined,
) => {
  const defaultMainCategories = await getStory<ProductListingPageStoryblok>({
    slugOrId: "/products/",
  });

  const [products, category] = await Promise.all([
    getDepictProducts(options, null),
    fetchDepictCategory(options),
  ]);

  const mainCategories =
    categories && categories.length > 0
      ? formatSBCategories(categories)
      : formatSBCategories(defaultMainCategories?.content?.categories);

  return {
    products,
    category,
    mainCategories,
  };
};

export const getDepictProducts = dedupe(
  async (options: DepictOptions, cursor: string | null) => {
    if (options.category) {
      const data = await getDepictProductsByCategory(options, cursor);
      return formatPlpData(data);
    } else if (options.query) {
      const data = await getDepictProductsByQuery(options, cursor);
      return formatPlpData(data);
    }
    return formatPlpData(null);
  },
);

export type DepictProductResponse_internal = Awaited<
  ReturnType<typeof getDepictProducts>
>;
